<template>
  <v-flex>
    <division-form
      v-model="showForm"
      v-if="showForm"
      :division-id="selectedDivision"
      @save="onFormSave"
    />
    <section v-show="!showForm">
      <page-toolbar>
        <kendo-button icon="add" title="Add new" @click="addRecord" v-if="!isReadOnly">
        </kendo-button>
        <kendo-button type="button" title="Export to Excel" @click="exportToExcel">
          <i class="k-icon k-i-excel"></i>
        </kendo-button>
      </page-toolbar>
      <div style="height:calc(100vh - 120px)" ref="divisionGrid"></div>
    </section>
  </v-flex>
</template>

<script>
import $ from '@/helpers/jquery-bridge'
import {jsonDropDownEditor} from '@/helpers/kendo-editors'
import {createCheckboxColumn} from '@/helpers/kendo-editors'
import {numericEditor} from '@/helpers/kendo-editors'
import {kendoMultiSelect} from '@/helpers/kendo-editors'
import {getDataItem} from '@/helpers/kendo-helpers'
import {mapGetters} from 'vuex'
import PageToolbar from '@/components/PageToolbar'
import DivisionForm from './Form'
import security from '@/mixins/security'

export default {
  mixins: [security],
  data() {
    return {
      showForm: false,
    }
  },
  mounted() {
    let vm = this
    let grid = $(this.$refs.divisionGrid)
      .kendoGrid({
        dataSource: new kendo.data.DataSource({
          type: 'aspnetmvc-ajax',
          serverPaging: true,
          serverFiltering: true,
          serverSorting: true,
          pageSize: 15,
          transport: {
            read: {
              type: 'GET',
              url: '/api/config/division',
              data: () => ({clientId: this.clientId}),
              dataType: 'json',
            },
            destroy: {
              type: 'DELETE',
              url: '/api/config/division',
            },
          },
          schema: {
            data: 'data',
            errors: 'errors',
            total: 'total',
            model: {
              id: 'divisionId',
              fields: {
                divisionName: {type: 'string', validation: {required: true}},
                legalName: {type: 'string', validation: {required: true}},
                sectorId: {type: 'number', validation: {required: true}},
                sectorCode: {type: 'string', validation: {required: true}},
                baseCcy: {type: 'string', validation: {required: true}},
                allowedTradeEntry: {type: 'boolean', defaultValue: true, editable: true},
                creditFacility: {type: 'boolean', defaultValue: false, editable: true},
                account: {type: 'boolean', defaultValue: false, editable: true},
                users: {type: 'object', defaultValue: []},
                usersExport: {type: 'string'},
                monthsAccountReceivable: {type: 'number'},
                canDelete: {type: 'boolean', defaultValue: true, editable: false},
              },
            },
          },
          error: function() {
            grid.one('dataBinding', e => {
              e.preventDefault()
            })
          },
        }),
        editable: !vm.isReadOnly && 'inline',
        filterable: true,
        sortable: true,
        pageable: true,
        edit: this.onEdit,
        columns: [
          {
            command: [{name: 'edit'}],
            width: 82,
            attributes: {style: 'text-align: center;'},
            hidden: vm.isReadOnly,
          },
          {
            title: 'Division',
            field: 'divisionName',
            width: 125,
            attributes: {style: 'text-align: left;'},
          },
          {
            title: 'Legal Name',
            field: 'legalName',
            width: 150,
            attributes: {style: 'text-align: left;'},
          },
          {
            title: 'Sector',
            field: 'sectorCode',
            width: 160,
            template: '#=sectorCode#',
            editor: jsonDropDownEditor(
              'sectorId',
              'sectorCode',
              '/api/config/division/getSectors?clientId=' + this.clientId,
              {},
              false,
            ),
          },
          {
            title: 'Base Ccy',
            field: 'baseCcy',
            width: 100,
            editor: jsonDropDownEditor(
              'ccy',
              'ccy',
              '/api/config/division/getCurrencies?clientId=' + this.clientId,
              {},
              false,
            ),
          },
          createCheckboxColumn(
            $(this.$refs.divisionGrid),
            'allowedTradeEntry',
            {
              title: 'Allowed Trade Entry',
              width: 132,
            },
            vm.isReadOnly,
          ),
          {
            title: 'Users',
            field: 'users',
            width: 320,
            attributes: {class: 'truncate-grid-column-text'},
            template: dataItem => (dataItem.users || []).map(el => el.userName).join(', '),
            editor: kendoMultiSelect(
              'userId',
              'userName',
              '/api/config/division/getUsers?clientId=' + this.clientId,
              {},
              true,
              false,
            ),
          },
          {
            title: 'Months Account Receivable',
            field: 'monthsAccountReceivable',
            width: 100,
            attributes: {style: 'text-align: right;'},
            format: '{0:n0}',
            editor: numericEditor({decimals: 0, format: '{0:n0}', spinners: false}),
          },
          {
            command: [{name: 'destroy', visible: dataItem => dataItem.canDelete}],
            width: 82,
            attributes: {style: 'text-align: center;'},
            hidden: vm.isReadOnly,
          },
        ],
        detailInit: detailInit,
        detailCollapse: () => grid.dataSource.read(),
        detailExpand: container => container.masterRow.find('.k-grid-delete').hide(),
      })
      .getKendoGrid()

    function detailInit(expandElement) {
      const divisionId = expandElement.data.divisionId
      let detailDataSource = new kendo.data.DataSource({
        type: 'aspnetmvc-ajax',
        serverPaging: true,
        serverSorting: true,
        serverFiltering: true,
        pageSize: 20,
        transport: {
          create: {
            type: 'POST',
            url: '/api/config/division/createDetail',
            dataType: 'json',
          },
          read: {
            type: 'GET',
            url: '/api/config/division/readDetail?divisionId=' + divisionId,
            dataType: 'json',
          },
          update: {
            type: 'PUT',
            url: '/api/config/division/updateDetail',
            dataType: 'json',
          },
          destroy: {
            type: 'DELETE',
            url: '/api/config/division/deleteDetail',
          },
        },
        schema: {
          data: 'data',
          errors: 'errors',
          total: 'total',
          model: {
            id: 'divisionCcyLinkId',
            fields: {
              divisionCcyLinkId: {type: 'number', defaultValue: 0},
              ccy: {type: 'string'},
              divisionId: {
                type: 'number',
                editable: false,
                defaultValue: divisionId,
              },
              vcyPairId: {type: 'number', validation: {required: true}},
              pairCode: {type: 'string', validation: {required: true}},
              canDelete: {type: 'bool', defaultValue: true, editable: false},
            },
          },
        },
        error: function() {
          grid.one('dataBinding', e => {
            e.preventDefault()
          })
        },
      })

      $("<div style='width: 400px;' id='detail_" + divisionId + "' class='k-content' />")
        .appendTo(expandElement.detailCell)
        .kendoGrid({
          dataSource: detailDataSource,
          scrollable: true,
          sortable: false,
          toolbar: !vm.isReadOnly ? ['create'] : '',
          editable: !vm.isReadOnly && 'inline',
          pageable: true,
          columns: [
            {
              command: [{name: 'edit'}],
              width: 82,
              attributes: {style: 'text-align: center;'},
              hidden: vm.isReadOnly,
            },
            {
              title: 'Com.Ccy Pair',
              field: 'pairCode',
              width: 100,
              editor: function(container, options) {
                const divisionCcyLinkId = options.model.divisionCcyLinkId
                const ccy = expandElement.data.baseCcy
                const clientId = vm.clientId
                return jsonDropDownEditor(
                  'ccyPairId',
                  'pairCode',
                  '/api/config/division/getCcyPairs?divisionId=' +
                    divisionId +
                    '&divisionCcyLinkId=' +
                    divisionCcyLinkId +
                    '&ccy=' +
                    ccy +
                    '&clientId=' +
                    clientId,
                  {
                    filter: 'contains',
                    change: function(elementChange) {
                      const dataItem = getDataItem(elementChange.sender.element)
                      const dropDownDataItem = elementChange.sender.dataItem()
                      const text = elementChange.sender.text()
                      const value = elementChange.sender.value()
                      dataItem.set('pairCode', text)
                      dataItem.set('ccyPairId', value)
                      if (dropDownDataItem) {
                        dataItem.set('ccy', dropDownDataItem.foreignCcy)
                      }
                    },
                  },
                  false,
                ).call(this, container, options)
              },
            },
            {
              title: 'Exposure Ccy',
              field: 'ccy',
              width: 82,
              headerAttributes: {style: 'text-align: center;'},
              editor: function(container, options) {
                container.html('<span data-bind="text: ' + options.field + '"></span>')
              },
            },
            {
              command: [{name: 'destroy', visible: dataItem => dataItem.canDelete}],
              width: 82,
              attributes: {style: 'text-align: center;'},
              hidden: vm.isReadOnly,
            },
          ],
        })
    }
  },
  methods: {
    exportToExcel() {
      const url = `/api/config/division/exportToExcel?clientId=${this.clientId}&t=${
        this.tokenString
      }`
      location.href = url
    },
    addRecord() {
      this.selectedDivision = -1
      this.showForm = true
    },
    onFormSave() {
      let grid = $(this.$refs.divisionGrid).getKendoGrid()
      grid.dataSource.read()
    },
    onEdit(e) {
      this.selectedDivision = e.model.divisionId
      this.showForm = true
      e.sender.cancelChanges()
    },
  },
  computed: {
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
    ...mapGetters({tokenString: 'authentication/tokenString'}),
  },
  components: {
    PageToolbar,
    DivisionForm,
  },
}
</script>
<style></style>
